/**
 * Navbar Invariants
 *
 * Enforces:
 * - Permissions (who can control navbar state)
 * - State normalization
 */

import { Invariants } from '@commandDrivenStateEngine/core/invariants';
import { NavbarState } from './state';
import { NavbarCommand } from './commands';

/**
 * Normalize state for consistency.
 */
const normalize = ( state: NavbarState ): NavbarState => {
  // No normalization needed for navbar state currently
  return state;
};

/**
 * Permission control - who can execute which commands.
 *
 * Source hierarchy:
 * - 'user': Can control all navbar UI (menus, drawer, QR modal)
 * - 'ai': Cannot control navbar UI (no access)
 * - 'api': Can update account data and QR image (from subscriptions/API calls)
 */
const canApply = ({
  command,
  meta
}: {
  state: NavbarState;
  command: NavbarCommand;
  meta: import( '@commandDrivenStateEngine/core/types' ).CommandMeta;
}): boolean => {
  const { source } = meta;
  const { type } = command;

  // API source: can update account data and QR image
  if ( source === 'api' ) {
    return (
      type === 'navbar/account/update' ||
      type === 'navbar/qrModal/setImage' ||
      type === 'navbar/qrModal/setLoading'
    );
  }

  // AI source: cannot control navbar UI
  if ( source === 'ai' ) {
    return false;
  }

  // User source: can control all navbar UI
  if ( source === 'user' ) {
    return (
      type.startsWith( 'navbar/drawer/' ) ||
      type.startsWith( 'navbar/menu/' ) ||
      type.startsWith( 'navbar/qrModal/' )
    );
  }

  // Unknown sources: deny
  return false;
};

export const invariants: Invariants<NavbarState, NavbarCommand> = {
  normalize,
  canApply
};
